/**
 * Fields shown as chips rather than as {tokens}.
 *
 * `{identity}` in the middle of a sentence does not read as "a value goes
 * here" to anyone who was not told, and half a token left behind after an
 * edit is worse. So the editor shows a chip carrying the field's name, and
 * the {token} form is what gets stored and sent.
 *
 * The chip is contenteditable="false", which is what makes it behave as one
 * thing: backspace removes the whole chip, the caret steps over it rather
 * than into it, and copy/cut carry it intact.
 *
 * Styles are inline because this HTML travels - into a v-model, out to the
 * server, back into another editor - where no stylesheet of ours follows.
 */

export const CHIP_CLASS = 'app-field-chip';
export const CHIP_LABEL_CLASS = 'app-field-chip__label';
export const CHIP_REMOVE_CLASS = 'app-field-chip__x';

// Kept deliberately short: an inline-block taller than the line it sits on
// pushes the following line away and ends up touching whatever is under it.
const CHIP_STYLE = [
  'display:inline-block',
  'padding:0 8px',
  'margin:0 2px',
  'border-radius:10px',
  'background:#e3f2fd',
  'color:#0d47a1',
  'border:1px solid #90caf9',
  'font-size:.9em',
  'line-height:1.45',
  'vertical-align:baseline',
  'white-space:nowrap',
  'cursor:pointer',
  'user-select:all',
  '-webkit-user-select:all'
].join(';');

/** Escape for use inside an attribute or as text. */
function escapeHtml(value) {
  return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * @param {{value: string, text: string}} token
 * @returns {string} the chip markup for one field
 */
/**
 * What a chip shows: the field's value where there is one, since that is what
 * the reader of the finished letter will see. The field's name is the tooltip,
 * for when the value alone does not say which field it came from.
 *
 * @param {{value: string, text: string, resolved?: string}} token
 * @returns {{shown: string, title: string}}
 */
function chipFace(token) {
  const value = String(token.resolved == null ? '' : token.resolved).trim();
  const name = token.text || token.value;
  // A field with nothing behind it prints a dash, the way an empty figure does
  // everywhere else here - an empty chip would be invisible and unclickable.
  // Which field it is stays in the tooltip either way.
  return {shown: value === '' ? '-' : value, title: name};
}

export function chipHtml(token) {
  const key = escapeHtml(token.value);
  const face = chipFace(token);
  // The label sits in its own span so changing the field does not wipe the
  // remove button, and the ✕ is hidden until the chip is hovered (see the
  // unscoped rules in RichTextInput).
  return `<span class="${CHIP_CLASS}" data-token="${key}" contenteditable="false"`
      + ` title="${escapeHtml(face.title)}" style="${CHIP_STYLE}">`
      + `<span class="${CHIP_LABEL_CLASS}">${escapeHtml(face.shown)}</span>`
      + `<span class="${CHIP_REMOVE_CLASS}" title="حذف">✕</span>`
      + `</span>`;
}

/**
 * Point an existing chip at a different field, keeping its ✕.
 *
 * @param {HTMLElement} chip
 * @param {object} token
 */
export function retargetChip(chip, token) {
  const face = chipFace(token);
  chip.setAttribute('data-token', token.value);
  chip.setAttribute('title', face.title);
  chipLabel(chip).textContent = face.shown;
}

/**
 * @param {HTMLElement} chip
 * @returns {HTMLElement} the element holding the chip's visible name
 */
export function chipLabel(chip) {
  return chip.querySelector('.' + CHIP_LABEL_CLASS) || chip;
}

/**
 * Chips back to the {token} text that gets stored and merged.
 *
 * Done through the DOM rather than with a regular expression: the chip's own
 * label may contain anything, and matching tags with a pattern is how you end
 * up eating the wrong span.
 *
 * @param {string} html
 * @returns {string}
 */
export function chipsToTokens(html) {
  if (!html || String(html).indexOf(CHIP_CLASS) === -1) return html || '';

  const holder = document.createElement('div');
  holder.innerHTML = html;
  holder.querySelectorAll('.' + CHIP_CLASS).forEach(chip => {
    const key = chip.getAttribute('data-token');
    // A chip with no field left on it is not a field; drop the wrapper and
    // keep its name, minus the remove button.
    const replacement = key ? '{' + key + '}' : (chipLabel(chip).textContent || '');
    chip.parentNode.replaceChild(document.createTextNode(replacement), chip);
  });
  // The zero-width spaces that gave the caret somewhere to sit after a chip
  // are scaffolding, not content.
  return holder.innerHTML.replace(/​/g, '');
}

/**
 * {tokens} to chips, for text coming back from the server.
 *
 * Only known fields become chips. Anything else stays exactly as typed - a
 * stray {word} is the author's text, not a field, and silently swallowing it
 * would be worse than leaving it alone.
 *
 * @param {string} html
 * @param {Array<{value: string, text: string}>} tokens
 * @returns {string}
 */
export function tokensToChips(html, tokens) {
  if (!html || !tokens || !tokens.length) return html || '';

  const byKey = {};
  tokens.forEach(t => { byKey[t.value] = t; });

  return String(html).replace(/\{([a-zA-Z0-9_]+)\}/g, (whole, key) =>
      byKey[key] ? chipHtml(byKey[key]) : whole);
}

/**
 * Repair chips that arrived by copy and paste.
 *
 * A pasted chip comes back through the browser's own sanitiser, which is free
 * to drop the contenteditable="false" and the inner spans - leaving a chip
 * that looks right but has no ✕ and behaves as ordinary text. Anything
 * carrying data-token is rebuilt here from the label it still has.
 *
 * @param {HTMLElement} root the editable element
 * @returns {boolean} whether anything needed fixing
 */
export function normalizeChips(root) {
  if (!root) return false;
  let repaired = false;

  root.querySelectorAll('[data-token]').forEach(chip => {
    if (!chip.classList.contains(CHIP_CLASS)) {
      chip.classList.add(CHIP_CLASS);
      repaired = true;
    }
    if (chip.getAttribute('contenteditable') !== 'false') {
      chip.setAttribute('contenteditable', 'false');
      repaired = true;
    }
    if (!chip.getAttribute('style')) {
      chip.setAttribute('style', CHIP_STYLE);
      repaired = true;
    }

    let label = chip.querySelector('.' + CHIP_LABEL_CLASS);
    if (!label) {
      // Its text is all that survived: put it back inside a label span.
      const text = (chip.textContent || '').replace(/✕/g, '').trim();
      chip.textContent = '';
      label = document.createElement('span');
      label.className = CHIP_LABEL_CLASS;
      label.textContent = text;
      chip.appendChild(label);
      repaired = true;
    }
    if (!chip.querySelector('.' + CHIP_REMOVE_CLASS)) {
      const remove = document.createElement('span');
      remove.className = CHIP_REMOVE_CLASS;
      remove.setAttribute('title', 'حذف');
      remove.textContent = '✕';
      chip.appendChild(remove);
      repaired = true;
    }
  });

  return repaired;
}

/**
 * @param {EventTarget} target
 * @returns {HTMLElement|null} the chip the event happened in, if any
 */
export function chipFromEvent(target) {
  if (!target || !target.closest) return null;
  return target.closest('.' + CHIP_CLASS);
}

/**
 * @param {EventTarget} target
 * @returns {boolean} whether the ✕ was what got clicked
 */
export function isChipRemove(target) {
  return !!(target && target.closest && target.closest('.' + CHIP_REMOVE_CLASS));
}
